import { cn } from "../utils/cn.js";
import type { Mood } from "../moods.js";

export interface MoodDotProps {
  mood: Mood;
  active?: boolean;
  /** Pixel diameter. Default 48. */
  size?: number;
  onClick?: () => void;
}

/**
 * MoodDot — round, glossy color button representing a feeling.
 *
 * Inactive: 1px shadow.
 * Active:   double ring (cream offset + teal outer) per the brand
 *           reference; we use a Tailwind ring for both layers since
 *           ring + ring-offset stacks cleanly.
 */
export const MoodDot = ({ mood, active = false, size = 48, onClick }: MoodDotProps) => (
  <button
    type="button"
    onClick={onClick}
    className="flex shrink-0 flex-col items-center gap-2 bg-transparent p-1"
    aria-pressed={active}
    aria-label={`${mood.name} — ${mood.tone}`}
  >
    <span
      className={cn(
        // Soothing select: shadow + a gentle lift, both eased.
        "relative block rounded-pill transition-[box-shadow,transform] duration-300 ease-soft motion-reduce:transition-none",
        mood.bg,
        active
          ? "scale-[1.06] shadow-[0_0_0_3px_rgb(var(--wo-cream)),0_0_0_5px_rgb(var(--wo-teal))]"
          : "shadow-1 hover:scale-[1.03]",
      )}
      style={{ width: size, height: size }}
    >
      {/* Soft top-left highlight — gives the dot a glossy feel without an SVG */}
      <span
        aria-hidden="true"
        className="absolute rounded-pill bg-white/30 blur-[1.5px]"
        style={{
          top: size * 0.18,
          left: size * 0.22,
          width: size * 0.24,
          height: size * 0.24,
        }}
      />
    </span>
    <span
      className={cn(
        "text-meta font-semibold",
        active ? "text-ink" : "text-ink-soft",
      )}
    >
      {mood.name}
    </span>
  </button>
);
